import { ref, readonly } from 'vue';

/**
 * @typedef {Object} useAsyncGuardOptions
 * @property {number} [throttleDelay=2000] - Time in milliseconds to keep the loading state after function execution.
 */

/**
 * @typedef {Object} useAsyncGuardReturn
 * @property {boolean} isLoading - Read-only reactive state indicating if the async function is in progress.
 * @property {Function: Promise<void>} runWithLock - Wrapper that runs with async lock and cooldown. Accepts a function that returns a Promise.
 */

/**
 * Composable for managing async function state with throttle
 * @param {useAsyncGuardOptions} [options={}] - Composable options
 * @returns {useAsyncGuardReturn} - Composable utilities
 */
export function useAsyncGuard(options = {}) {
  const isLoading = ref(false);
  const throttleDelay = options.throttleDelay || 2000;

  const runWithLock = async fn => {
    if (isLoading.value) return;

    isLoading.value = true;
    try {
      await fn();
    } finally {
      // Keep the loading state for a short duration to prevent rapid re-trigger
      setTimeout(() => {
        isLoading.value = false;
      }, throttleDelay);
    }
  };

  return {
    isLoading: readonly(isLoading),
    runWithLock,
  };
}
